import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, FileText, Layers, Zap, CornerDownLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { buildIndex, pageKeyForPath, scoreRecord } from '../lib/searchIndex';

const KIND_ICON = { page: FileText, custom: Layers, action: Zap };

function Highlight({ text, term }) {
  if (!text) return null;
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const t = term.toLowerCase();
  const parts = [];
  let i = 0;
  let idx = lower.indexOf(t, i);
  let k = 0;
  while (idx !== -1) {
    if (idx > i) parts.push(<span key={k++}>{text.slice(i, idx)}</span>);
    parts.push(
      <mark key={k++} style={{ background: 'rgba(201,161,59,0.4)', color: 'inherit', padding: 0 }}>
        {text.slice(idx, idx + t.length)}
      </mark>
    );
    i = idx + t.length;
    idx = lower.indexOf(t, i);
  }
  if (i < text.length) parts.push(<span key={k++}>{text.slice(i)}</span>);
  return <>{parts}</>;
}

export default function SearchBar() {
  const { customPages, canAccess, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);

  const index = useMemo(() => buildIndex(customPages), [customPages]);

  const currentPageKey = useMemo(
    () => pageKeyForPath(index, location.pathname),
    [index, location.pathname]
  );

  // Role-aware visibility: only pages/actions the current user may access.
  const visible = useCallback(
    (r) => {
      if (r.kind === 'action') {
        if (r.always) return true;
        if (r.requiresUser) return !!user;
        return canAccess(r.pageKey);
      }
      return canAccess(r.pageKey);
    },
    [canAccess, user]
  );

  const { onPage, elsewhere, ordered } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { onPage: [], elsewhere: [], ordered: [] };
    const scored = [];
    for (const r of index) {
      if (!visible(r)) continue;
      const res = scoreRecord(r, q);
      if (res) scored.push(res);
    }
    scored.sort((a, b) => b.score - a.score);
    const on = scored.filter((r) => currentPageKey && r.pageKey === currentPageKey);
    const rest = scored.filter((r) => !(currentPageKey && r.pageKey === currentPageKey));
    return { onPage: on, elsewhere: rest, ordered: [...on, ...rest] };
  }, [query, index, visible, currentPageKey]);

  useEffect(() => setActive(0), [query]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Keep the panel viewport-bound (fixes off-screen clipping on mobile).
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(window.innerWidth - 16, 480);
      setPos({ top: Math.round(r.bottom + 6), right: 8, width });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open]);

  const go = useCallback(
    (r) => {
      if (!r) return;
      setOpen(false);
      setQuery('');
      navigate(r.path);
    },
    [navigate]
  );

  const onInputKey = (e) => {
    if (!ordered.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, ordered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(ordered[active]);
    }
  };

  const renderRow = (r, flatIndex) => {
    const Icon = KIND_ICON[r.kind] || FileText;
    const isActive = flatIndex === active;
    return (
      <button
        key={r.kind + r.pageKey}
        type="button"
        data-testid={`search-result-${flatIndex}`}
        onMouseEnter={() => setActive(flatIndex)}
        onClick={() => go(r)}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors"
        style={{ background: isActive ? 'var(--sage-light, rgba(198,202,187,0.35))' : 'transparent' }}
      >
        <Icon size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--maroon)' }} />
        <span className="min-w-0">
          <span className="block text-[14px] font-title" style={{ color: 'var(--ink)' }}>
            <Highlight text={r.title} term={query.trim()} />
          </span>
          {r.snippet && (
            <span className="block text-xs mt-0.5 leading-snug" style={{ color: 'var(--olive)' }}>
              <Highlight text={r.snippet} term={query.trim()} />
            </span>
          )}
        </span>
      </button>
    );
  };

  let flat = -1;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="site-search-toggle"
        aria-label="Search the site"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded transition-colors hover:bg-[var(--navy-dark)]"
        style={{ color: 'var(--cream)' }}
      >
        <Search size={16} />
        <span className="hidden xl:inline text-[13px]">Search</span>
      </button>

      {open && (
        <div
          data-testid="site-search-panel"
          className="z-[60] rounded-lg shadow-2xl overflow-hidden"
          style={{
            position: 'fixed',
            top: pos ? pos.top : 64,
            right: pos ? pos.right : 8,
            width: pos ? pos.width : 360,
            background: 'var(--cream, #f4f1e8)',
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
            <Search size={16} style={{ color: 'var(--olive)' }} />
            <input
              ref={inputRef}
              data-testid="site-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Search this page and the site…"
              className="flex-1 bg-transparent outline-none text-[14px]"
              style={{ color: 'var(--ink)' }}
            />
            {query && (
              <button type="button" data-testid="site-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                <X size={15} style={{ color: 'var(--olive)' }} />
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="site-search-results">
            {!query.trim() ? (
              <p className="px-3 py-4 text-xs" style={{ color: 'var(--olive)' }}>
                Type to search. Matches on the current page appear first, then across the rest of the site.
              </p>
            ) : ordered.length === 0 ? (
              <p className="px-3 py-4 text-sm" data-testid="search-no-results" style={{ color: 'var(--olive)' }}>
                No results for “{query.trim()}”.
              </p>
            ) : (
              <>
                {onPage.length > 0 && (
                  <div>
                    <p className="px-3 pt-2.5 pb-1 text-[11px] uppercase tracking-wide font-title" style={{ color: 'var(--navy)' }}>
                      On this page
                    </p>
                    {onPage.map((r) => { flat += 1; return renderRow(r, flat); })}
                  </div>
                )}
                {elsewhere.length > 0 && (
                  <div>
                    <p className="px-3 pt-2.5 pb-1 text-[11px] uppercase tracking-wide font-title" style={{ color: 'var(--navy)' }}>
                      {onPage.length > 0 ? 'Across the site' : 'Results'}
                    </p>
                    {elsewhere.map((r) => { flat += 1; return renderRow(r, flat); })}
                  </div>
                )}
              </>
            )}
          </div>

          {ordered.length > 0 && (
            <div className="px-3 py-1.5 border-t flex items-center gap-1 text-[11px]" style={{ borderColor: 'rgba(0,0,0,0.1)', color: 'var(--olive)' }}>
              <CornerDownLeft size={12} /> Enter to open · ↑↓ to navigate · Esc to close
            </div>
          )}
        </div>
      )}
    </div>
  );
}
